import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  styled,
  Pagination,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getApiData } from "../../utils/APIHelper";
import { Setting } from "../../utils/Setting";
import ProposalDetails from "./components/ProposalDetails";
import MobileProjectCards from "./components/MobileProjectCards";
import ProjectViewToggle from "./components/ProjectViewToggle";
import ProjectStats from "./components/ProjectStats";
import { updateUserData } from "../../utils/CommonFunction";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import {
  VIEW_MODES,
  flattenProjectsToScopeRows,
  parseViewMode,
} from "./utils/projectViewHelpers";

const StyledHeading = styled("h2")({
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  fontSize: "24px",
  fontWeight: "500",
});

export default function Projects() {
  const { userData } = useSelector((state) => state.auth);
  const contractorId = userData?.contractor_data?.id;
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const viewMode = parseViewMode(searchParams.get("view"));

  // Project stats (header summary shown above the list).
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  const isScopesView = viewMode === VIEW_MODES.SCOPES;

  const displayDetails = useMemo(() => {
    if (isScopesView) {
      return flattenProjectsToScopeRows(details);
    }
    return details;
  }, [details, isScopesView]);

  const fetchData = async (currentPage, mode) => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        filter: "ongoing,delivered,in_warranty,completed,ready_to_start",
      });

      if (mode === VIEW_MODES.SCOPES) {
        params.set("per_page", "1000");
      } else {
        params.set("per_page", "11");
        if (currentPage !== 1) params.set("page", String(currentPage));
      }

      const url = `${Setting.endpoints.contractorProjects}?${params.toString()}`;
      const response = await getApiData(url, "GET", {}, {}, false);
      const newDetails = response?.data || [];
      setTotalPages(response?.total_pages || 0);
      setDetails(newDetails);
    } catch (error) {
      console.error("Something went wrong", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!contractorId) return;
    setStatsLoading(true);
    try {
      const url = `${Setting.endpoints.projectAnalytics}?filter=ongoing,delivered,in_warranty,completed,ready_to_start`;
      const response = await getApiData(url, "GET", {}, {}, false);
      if (response?.success) {
        setStats(response?.data || null);
      }
    } catch (error) {
      console.error("Error fetching project stats", error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    updateUserData();
  }, []);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorId]);

  useEffect(() => {
    fetchData(page, viewMode);
  }, [page, contractorId, viewMode]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleViewModeChange = (nextMode) => {
    if (nextMode === viewMode) return;
    setPage(1);
    const nextParams = new URLSearchParams(searchParams);
    if (nextMode === VIEW_MODES.SCOPES) {
      nextParams.set("view", VIEW_MODES.SCOPES);
    } else {
      nextParams.delete("view");
    }
    setSearchParams(nextParams, { replace: true });
  };

  const paginationBarSx = {
    position: "sticky",
    bottom: 20,
    left: 0,
    right: 0,
    zIndex: 5,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    background: "#fff",
    padding: "12px 0",
    marginTop: "auto",
  };

  const emptyMessage = isScopesView
    ? "No scopes available"
    : "No data available";

  const showEmptyState = !loading && displayDetails?.length === 0;

  const statsBar = (
    <Box sx={{ padding: isMobile ? "0 0 16px" : "0 2vw 16px" }}>
      <ProjectStats stats={stats} loading={statsLoading} />
    </Box>
  );

  const DesktopView = () => (
    <>
      <ProposalDetails details={displayDetails} viewMode={viewMode} />
      {!isScopesView && (
        <Box sx={paginationBarSx}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </>
  );

  const MobileView = () => (
    <>
      <MobileProjectCards
        details={displayDetails}
        loading={loading}
        viewMode={viewMode}
        emptyMessage={emptyMessage}
      />
      {!isScopesView && (
        <Box sx={paginationBarSx}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </>
  );

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          typography: "body1",
          backgroundColor: "#fff",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          borderRadius: "6px",
          fontFamily: "Inter Tight",
        }}
      >
        <SecurityPolicyModal
          open={showSecurityModal}
          onConfirm={handleSecurityModalClose}
        />

        {isMobile ? (
          <div
            style={{
              padding: "1rem",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                padding: "2vh 2vw 2vh 2vw",
              }}
            >
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: "600",
                  color: "#0D0D0E",
                  fontFamily: "Inter Tight",
                  letterSpacing: "0",
                }}
              >
                Projects
              </Typography>
              <ProjectViewToggle
                viewMode={viewMode}
                onChange={handleViewModeChange}
              />
            </Box>

            {statsBar}

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 200,
                }}
              >
                <CircularProgress />
              </Box>
            ) : showEmptyState ? (
              <Typography
                variant="body1"
                sx={{ textAlign: "center", marginTop: 4 }}
              >
                {emptyMessage}
              </Typography>
            ) : (
              <MobileView />
            )}
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "4vh 2vw 2vh 2vw",
                background:
                  "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
              }}
            >
              <StyledHeading>Projects</StyledHeading>
              <ProjectViewToggle
                viewMode={viewMode}
                onChange={handleViewModeChange}
              />
            </div>

            {statsBar}

            <Box
              sx={{
                padding: "0 1.5vw",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : showEmptyState ? (
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", marginTop: 4 }}
                >
                  {emptyMessage}
                </Typography>
              ) : (
                <DesktopView />
              )}
            </Box>
          </>
        )}
      </Box>
    </div>
  );
}
